function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import ServiceFactory from "../../service";

/*
 * Google Analytics import (Manage Data > Connect). Two jobs on one connection:
 *  - Import history: GA4 daily history from BEFORE Plunk tracking started (the
 *    server ends the window the day before tracking began).
 *  - Fill a tracking gap: a stretch with no live tracking, filled from GA4 hour by
 *    hour. Only minutes with no live tracking are filled; each fill is tagged with
 *    an id so it can be removed without touching live data.
 * Backend: /v1/data-feeds/schedules (connection), /coverage (live tracking per hour +
 * detected gaps), /backfills (list, preview, fill, remove; owner/admin only), /runs.
 * Copy rule: plain words, no em-dashes, nothing specific to one tenant or incident.
 */

export const SA_EMAIL = "plunksearch@plunksearch.iam.gserviceaccount.com";
function svc(api) {
  const Service = ServiceFactory.getType(`/data-feeds${api}`, "product");
  return new Service();
}
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
function parseSettings(s) {
  try {
    return JSON.parse(s && s.settings || "{}");
  } catch (e) {
    return {};
  }
}
const TIME_ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "America/Toronto", "Europe/London", "Europe/Berlin", "Australia/Sydney", "UTC"];

// ── time zone math (browser Intl; no library) ──
function tzOffsetMin(tz, date) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  const p = {};
  dtf.formatToParts(date).forEach(x => {
    p[x.type] = x.value;
  });
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return Math.round((asUtc - date.getTime()) / 60000);
}
export function zonedToUtc(dateStr, timeStr, tz) {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const off = tzOffsetMin(tz, new Date(guess));
  let ts = guess - off * 60000;
  const off2 = tzOffsetMin(tz, new Date(ts));
  if (off2 !== off) ts = guess - off2 * 60000;
  return new Date(ts);
}
export function utcToZoned(date, tz) {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
  const p = {};
  dtf.formatToParts(date).forEach(x => {
    p[x.type] = x.value;
  });
  return {
    date: `${p.year}-${p.month}-${p.day}`,
    time: `${String(+p.hour % 24).padStart(2, "0")}:${p.minute}`
  };
}
const isoZ = d => d.toISOString().replace(/\.\d{3}Z$/, "Z");
function fmtZoned(iso, tz) {
  if (!iso) return "";
  const z = utcToZoned(new Date(iso), tz);
  const d = new Date(z.date + "T00:00:00Z");
  return `${d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  })} ${z.time}`;
}
const n0 = v => v == null ? "" : Number(v).toLocaleString();
function tzLabel(tz) {
  try {
    const off = tzOffsetMin(tz, new Date());
    const h = Math.trunc(Math.abs(off) / 60),
      m = Math.abs(off) % 60;
    return `${tz} (UTC${off < 0 ? "-" : "+"}${h}${m ? ":" + String(m).padStart(2, "0") : ""})`;
  } catch (e) {
    return tz;
  }
}
function lsGet(k, f) {
  try {
    return localStorage.getItem(k) || f;
  } catch (e) {
    return f;
  }
}
function lsSet(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch (e) {/* ignore */}
}
const S = {
  inp: {
    width: "100%",
    padding: "7px 9px",
    fontSize: "13px",
    border: "1px solid #d5dbe3",
    borderRadius: "6px",
    marginTop: "4px",
    boxSizing: "border-box"
  },
  lab: {
    fontSize: "12px",
    display: "block",
    marginBottom: "8px"
  },
  hint: {
    color: "#6b7280",
    fontSize: "12px",
    margin: "0 0 10px"
  },
  warn: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    color: "#92400e",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "12px",
    margin: "8px 0"
  },
  ok: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    color: "#166534",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "12px",
    margin: "8px 0"
  },
  err: {
    color: "#dc2626",
    fontSize: "12px",
    margin: "6px 0"
  },
  th: {
    textAlign: "right",
    padding: "5px 8px",
    color: "#6b7280",
    fontWeight: 600,
    borderBottom: "1px solid #eef0f3",
    whiteSpace: "nowrap"
  },
  td: {
    textAlign: "right",
    padding: "5px 8px",
    borderBottom: "1px solid #f3f4f6",
    whiteSpace: "nowrap"
  },
  row: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap"
  },
  opt: on => ({
    flex: "1 1 220px",
    border: `1px solid ${on ? "#2563eb" : "#d5dbe3"}`,
    boxShadow: on ? "0 0 0 2px rgba(37,99,235,.15)" : "none",
    borderRadius: "8px",
    padding: "10px 12px",
    cursor: "pointer",
    background: "#fff"
  })
};
const TICK = {
  width: "auto",
  minWidth: 0,
  margin: "2px 0 0",
  flex: "0 0 auto",
  display: "inline-block",
  verticalAlign: "middle"
};
const BTN = "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base";
const BTNP = "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary";

// Hourly live-tracking bars around the window; the window is shaded.
function CoverageStrip({
  coverage,
  startUtc,
  endUtc
}) {
  if (!coverage || !coverage.hours || !coverage.hours.length) return null;
  const f = new Date(coverage.fromUtc).getTime();
  const ws = startUtc ? startUtc.getTime() : null,
    we = endUtc ? endUtc.getTime() : null;
  let a = 0,
    b = coverage.hours.length;
  if (ws && we) {
    a = Math.max(0, Math.floor((ws - f) / 3600e3) - 12);
    b = Math.min(coverage.hours.length, Math.ceil((we - f) / 3600e3) + 12);
  }
  const slice = coverage.hours.slice(a, b);
  if (!slice.length) return null;
  const max = Math.max(1, ...slice);
  const first = new Date(f + a * 3600e3),
    last = new Date(f + b * 3600e3);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      margin: "6px 0 2px"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "flex-end",
      gap: "1px",
      height: "40px",
      overflow: "hidden"
    }
  }, slice.map((v, i) => {
    const h0 = f + (a + i) * 3600e3,
      h1 = h0 + 3600e3;
    const inWin = ws && we && h1 > ws && h0 < we;
    const height = v > 0 ? Math.max(4, Math.round(36 * Math.sqrt(v / max))) : 3;
    return /*#__PURE__*/React.createElement("i", {
      key: i,
      title: `${new Date(h0).toISOString().slice(0, 13)}:00 UTC · ${v} views`,
      style: {
        flex: "1 1 0",
        minWidth: "1px",
        height: `${inWin ? 38 : height}px`,
        borderRadius: "1px",
        background: inWin ? v > 0 ? "#86b6f6" : "#bfd5fb" : v > 0 ? "#86c49b" : "#f3c6c6"
      }
    });
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "11px",
      color: "#6b7280"
    }
  }, /*#__PURE__*/React.createElement("span", null, first.toISOString().slice(5, 13).replace("T", " "), ":00 UTC"), /*#__PURE__*/React.createElement("span", null, last.toISOString().slice(5, 13).replace("T", " "), ":00 UTC")), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: "12px",
      fontSize: "11px",
      color: "#6b7280",
      flexWrap: "wrap"
    }
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("i", {
    style: {
      display: "inline-block",
      width: 9,
      height: 9,
      background: "#86c49b",
      marginRight: 4
    }
  }), "Live tracking"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("i", {
    style: {
      display: "inline-block",
      width: 9,
      height: 9,
      background: "#f3c6c6",
      marginRight: 4
    }
  }), "No tracking data"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("i", {
    style: {
      display: "inline-block",
      width: 9,
      height: 9,
      background: "#bfd5fb",
      marginRight: 4
    }
  }), "Your window")));
}
export class ImportWizard extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "copySa", () => {
      try {
        navigator.clipboard.writeText(SA_EMAIL);
      } catch (e) {/* ignore */}
      const t = document.querySelector("paper-toast");
      if (t) t.show("Service-account email copied.");
    });
    _defineProperty(this, "runHistory", async mode => {
      this.setState({
        working: true,
        error: ""
      });
      try {
        const id = await this.saveHistorySchedule();
        const before = await this.runIds(id);
        await svc(`/schedules/${id}/run${mode === "preview" ? "?mode=preview" : ""}`).postAsync({});
        if (mode !== "preview") this.setState({
          step: 4
        });
        this.pollNewRun(id, before, mode === "preview" ? "preview" : "full-refresh", run => {
          if (!this._alive) return;
          if (mode === "preview") this.setState({
            working: false,
            preview: run,
            step: 3,
            error: run.success ? "" : run.message || "Preview failed."
          });else this.setState({
            working: false,
            importRun: run,
            error: run.success ? "" : run.message || "Import failed."
          });
        });
      } catch (e) {
        this.setState({
          working: false,
          error: e && e.message || "Google didn't accept that connection. Check the service account is a Viewer on the property."
        });
      }
    });
    // ── GAP FILL ──
    _defineProperty(this, "loadGap", async () => {
      const [coverage, backfills] = await Promise.all([safe(svc("/coverage").getAsync(), null), safe(svc("/backfills").getAsync(), null)]);
      if (!this._alive) return;
      this.setState({
        coverage,
        backfills
      }, () => {
        const open = (coverage && coverage.gaps || []).findIndex(g => !(g.backfillIds || []).length);
        if (open >= 0) this.pickGap(open);else if (coverage && coverage.gaps && coverage.gaps.length) this.pickGap(0);
      });
    });
    _defineProperty(this, "runGap", async kind => {
      const {
        start,
        end
      } = this.windowUtc();
      if (!start || !end || end <= start) {
        this.setState({
          error: "Enter a start and an end, with the end after the start."
        });
        return;
      }
      if (end.getTime() > Date.now()) {
        this.setState({
          error: "The end can't be in the future."
        });
        return;
      }
      this.setState({
        working: true,
        error: "",
        ...(kind === "preview" ? {
          gPreview: null
        } : {
          gFill: null
        })
      });
      try {
        const before = this.state.schedule ? await this.runIds(this.state.schedule.id) : new Set();
        const res = await svc(kind === "preview" ? "/backfills/preview" : "/backfills").postAsync(this.gapBody());
        const sched = await this.refreshSchedule();
        const id = res && res.scheduleId || sched && sched.id;
        this.pollNewRun(id, before, kind === "preview" ? "gap-preview" : "gap-fill", run => {
          if (!this._alive) return;
          let summary = null;
          const m = String(run.message || "");
          const j = m.indexOf("{");
          if (run.success && j >= 0) {
            try {
              summary = JSON.parse(m.slice(j));
            } catch (e) {/* plain */}
          }
          const err = run.success ? "" : m.replace(/^BACKFILL_TABLES_MISSING:\s*/, "") || "Google didn't return data for that window.";
          if (kind === "preview") {
            if (summary && summary.timeZone && summary.timeZone !== this.state.tz) lsSet("plunkGa4Tz", summary.timeZone);
            this.setState({
              working: false,
              gPreview: summary ? {
                ...summary,
                _body: this.gapBody()
              } : null,
              error: err
            });
          } else {
            this.setState({
              working: false,
              gFill: summary || (run.success ? {} : null),
              error: err
            });
            this.loadGap();
          }
        });
      } catch (e) {
        this.setState({
          working: false,
          error: e && e.message || "Could not start. Try again."
        });
      }
    });
    _defineProperty(this, "removeBackfill", async id => {
      this.setState({
        working: true,
        error: "",
        confirmRemove: null
      });
      try {
        await svc("/backfills").deleteAsync(id);
        const tick = async n => {
          const bf = await safe(svc("/backfills").getAsync(), null);
          const item = bf && (bf.items || []).find(x => x.id === id);
          if (!this._alive) return;
          if (!item || item.status === "removed" || n > 60) {
            this.setState({
              backfills: bf,
              working: false
            });
            this.loadGap();
            return;
          }
          this._poll = setTimeout(() => tick(n + 1), 4000);
        };
        tick(0);
      } catch (e) {
        this.setState({
          working: false,
          error: e && e.message || "Could not remove."
        });
      }
    });
    const tz = lsGet("plunkGa4Tz", "America/New_York");
    this.state = {
      mode: props.initialMode || null,
      // "history" | "gap"
      step: 1,
      loading: true,
      schedule: null,
      propertyId: "",
      hasKey: false,
      editConn: false,
      saJson: "",
      // history
      months: 24,
      startDate: "",
      endDate: "",
      importConversions: true,
      importGeo: true,
      preview: null,
      importRun: null,
      // gap
      tz,
      coverage: null,
      backfills: null,
      gapIdx: 0,
      gStartDate: "",
      gStartTime: "",
      gEndDate: "",
      gEndTime: "",
      gPreview: null,
      gFill: null,
      confirmRemove: null,
      gConv: true,
      working: false,
      error: ""
    };
    this._alive = true;
  }
  async componentDidMount() {
    const schedules = await safe(svc("/schedules?provider=HistoricalImport").getAsync(), []);
    const sched = (schedules || []).find(s => s.provider === "HistoricalImport") || null;
    const st = parseSettings(sched);
    if (!this._alive) return;
    this.setState({
      schedule: sched,
      propertyId: st.Ga4PropertyId || "",
      hasKey: !!st.hasServiceAccount,
      loading: false
    });
    if (this.state.mode === "gap") this.loadGap();
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._poll) clearTimeout(this._poll);
  }

  // ── shared helpers ──
  // No key of their own is fine: the worker reads GA with the platform account (SA_EMAIL) they add as Viewer.
  connReady() {
    return !!this.state.propertyId.trim();
  }
  async runIds(scheduleId) {
    const runs = await safe(svc("/runs?top=30").getAsync(), []);
    return new Set((runs || []).filter(r => String(r.scheduleId) === String(scheduleId)).map(r => String(r.id)));
  }
  // Wait for a NEW run (not in `before`) on the schedule whose mode starts with `modePrefix` to finish.
  pollNewRun(scheduleId, before, modePrefix, onDone, tries = 0) {
    const tick = async () => {
      const runs = await safe(svc("/runs?top=30").getAsync(), []);
      const run = (runs || []).find(r => String(r.scheduleId) === String(scheduleId) && !before.has(String(r.id)) && String(r.mode || "").startsWith(modePrefix));
      if (run && run.finishedDate) {
        onDone(run);
        return;
      }
      if (!this._alive) return;
      if (tries > 240) {
        onDone({
          success: false,
          message: "This is taking longer than expected. Check the Feeds tab run history."
        });
        return;
      }
      this._poll = setTimeout(() => this.pollNewRun(scheduleId, before, modePrefix, onDone, tries + 1), 4000);
    };
    this._poll = setTimeout(tick, 3000);
  }
  renderConnection() {
    const {
      schedule,
      propertyId,
      hasKey,
      editConn,
      saJson
    } = this.state;
    if (schedule && propertyId && !editConn) {
      return /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          background: "#f8fafc",
          border: "1px solid #d5dbe3",
          borderRadius: "8px",
          padding: "8px 12px",
          marginBottom: "12px",
          fontSize: "13px"
        }
      }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "GA4 property ", propertyId), " \xB7 ", hasKey ? "your service-account key on file" : "read as " + SA_EMAIL), /*#__PURE__*/React.createElement("a", {
        className: "territory-toggle",
        style: {
          cursor: "pointer"
        },
        onClick: () => this.setState({
          editConn: true
        })
      }, "Change"));
    }
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: "10px"
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 6px",
        fontSize: "13px"
      }
    }, "In ", /*#__PURE__*/React.createElement("b", null, "GA4 Admin, Property access management"), ", add this email as ", /*#__PURE__*/React.createElement("b", null, "Viewer"), ":"), /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 10px"
      }
    }, /*#__PURE__*/React.createElement("code", {
      style: {
        background: "#f4f5f7",
        padding: "3px 8px",
        borderRadius: "4px",
        fontSize: "12px"
      }
    }, SA_EMAIL), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      style: {
        marginLeft: "8px"
      },
      onClick: this.copySa
    }, "Copy")), /*#__PURE__*/React.createElement("label", {
      style: S.lab
    }, "GA4 property ID", /*#__PURE__*/React.createElement("input", {
      style: S.inp,
      value: propertyId,
      placeholder: "e.g. 313456789",
      onChange: e => this.setState({
        propertyId: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", {
      style: S.lab
    }, hasKey ? "Your service-account key (JSON): a key is on file; paste only to replace it" : "Your own service-account key (JSON, optional): leave empty to use the email above", /*#__PURE__*/React.createElement("textarea", {
      style: {
        ...S.inp,
        height: "64px",
        resize: "vertical"
      },
      value: saJson,
      onChange: e => this.setState({
        saJson: e.target.value
      })
    })));
  }

  // ── HISTORY (import before tracking started) ──
  async saveHistorySchedule() {
    const s = this.state;
    const prev = parseSettings(s.schedule);
    const settings = {
      ...Object.fromEntries(Object.entries(prev).filter(([k]) => !/^has[A-Z]/.test(k) && !/^Ga4Gap/.test(k))),
      Ga4PropertyId: s.propertyId.trim(),
      Ga4MonthsBack: Number(s.months) || 24,
      Ga4ImportConversions: !!s.importConversions,
      Ga4ImportGeo: !!s.importGeo
    };
    if (s.saJson.trim()) settings.Ga4ServiceAccountJson = s.saJson.trim();
    if (s.startDate) settings.Ga4StartDate = s.startDate;else delete settings.Ga4StartDate;
    if (s.endDate) settings.Ga4EndDate = s.endDate;else delete settings.Ga4EndDate;
    const body = {
      provider: "HistoricalImport",
      mode: "full-refresh",
      frequency: "Daily",
      hour: 4,
      timezone: "UTC",
      status: "Inactive",
      settings: JSON.stringify(settings)
    };
    if (s.schedule) body.id = s.schedule.id;
    const saved = await svc("/schedules").postAsync(body);
    const sched = saved && saved.id ? saved : (await safe(svc("/schedules?provider=HistoricalImport").getAsync(), []))[0] || null;
    if (!sched) throw {
      message: "Could not save the connection."
    };
    const st = parseSettings(sched);
    this.setState({
      schedule: sched,
      hasKey: !!st.hasServiceAccount,
      saJson: "",
      editConn: false
    });
    return sched.id;
  }
  renderHistory() {
    const {
      step,
      working,
      error,
      preview,
      importRun
    } = this.state;
    if (step <= 2) {
      return /*#__PURE__*/React.createElement("div", null, this.renderConnection(), /*#__PURE__*/React.createElement("label", {
        style: S.lab
      }, "Months back", /*#__PURE__*/React.createElement("input", {
        style: {
          ...S.inp,
          width: "110px"
        },
        type: "number",
        min: "1",
        max: "48",
        value: this.state.months,
        onChange: e => this.setState({
          months: e.target.value
        })
      }), /*#__PURE__*/React.createElement("span", {
        style: {
          ...S.hint,
          marginLeft: "8px"
        }
      }, "24 recommended. 13 or more months makes forecasts seasonal.")), /*#__PURE__*/React.createElement("div", {
        style: S.row
      }, /*#__PURE__*/React.createElement("label", {
        style: {
          ...S.lab,
          flex: 1
        }
      }, "Start date (optional)", /*#__PURE__*/React.createElement("input", {
        style: S.inp,
        type: "date",
        value: this.state.startDate,
        onChange: e => this.setState({
          startDate: e.target.value
        })
      })), /*#__PURE__*/React.createElement("label", {
        style: {
          ...S.lab,
          flex: 1
        }
      }, "End date (optional)", /*#__PURE__*/React.createElement("input", {
        style: S.inp,
        type: "date",
        value: this.state.endDate,
        onChange: e => this.setState({
          endDate: e.target.value
        })
      }))), /*#__PURE__*/React.createElement("p", {
        style: S.hint
      }, "History always ends the day before your live tracking began. A later end date is moved back, and the preview shows the exact dates. To fill a stretch your tracking missed later on, use Fill a tracking gap."), /*#__PURE__*/React.createElement("label", {
        style: {
          fontSize: "13px",
          display: "block",
          marginBottom: "4px"
        }
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: this.state.importConversions,
        onChange: e => this.setState({
          importConversions: e.target.checked
        }),
        style: {
          ...TICK,
          marginRight: "8px"
        }
      }), "Include conversions (GA4 key events)"), /*#__PURE__*/React.createElement("label", {
        style: {
          fontSize: "13px",
          display: "block",
          marginBottom: "10px"
        }
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: this.state.importGeo,
        onChange: e => this.setState({
          importGeo: e.target.checked
        }),
        style: {
          ...TICK,
          marginRight: "8px"
        }
      }), "Include geography (city level; GA4 doesn't share postal codes)"), error && /*#__PURE__*/React.createElement("p", {
        style: S.err
      }, error), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          justifyContent: "space-between"
        }
      }, /*#__PURE__*/React.createElement("button", {
        className: BTN,
        disabled: working,
        onClick: () => this.setState({
          mode: null,
          step: 1,
          error: ""
        })
      }, "Back"), /*#__PURE__*/React.createElement("button", {
        className: BTNP,
        disabled: !this.connReady() || working,
        onClick: () => this.runHistory("preview")
      }, working ? "Checking with Google..." : "Preview import")));
    }
    if (step === 3) {
      return /*#__PURE__*/React.createElement("div", null, preview && preview.success ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
        style: {
          margin: "0 0 8px"
        }
      }, /*#__PURE__*/React.createElement("b", null, "Preview looks good.")), /*#__PURE__*/React.createElement("p", {
        style: {
          margin: "0 0 8px",
          fontSize: "13px"
        }
      }, String(preview.message || "").replace(/\s+—\s+/g, ": ").replace(/—/g, ":")), /*#__PURE__*/React.createElement("p", {
        style: S.hint
      }, "Nothing has been written yet. Live tracking owns every day from its start, so nothing is counted twice.")) : /*#__PURE__*/React.createElement("p", {
        style: S.err
      }, error || "Preview failed. Check the property ID and that the service account is a Viewer."), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          justifyContent: "space-between"
        }
      }, /*#__PURE__*/React.createElement("button", {
        className: BTN,
        onClick: () => this.setState({
          step: 2,
          preview: null
        })
      }, "Back"), preview && preview.success && /*#__PURE__*/React.createElement("button", {
        className: BTNP,
        onClick: () => this.runHistory("import")
      }, "Import history")));
    }
    return /*#__PURE__*/React.createElement("div", null, !importRun ? /*#__PURE__*/React.createElement("p", null, "Importing. This can take a few minutes; you can close this window and follow progress in the Feeds tab.") : importRun.success ? /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("b", null, "History imported."), " ", String(importRun.message || "").replace(/\s+—\s+/g, ": ").replace(/—/g, ":")) : /*#__PURE__*/React.createElement("p", {
      style: S.err
    }, error || "Import failed. See the Feeds tab run history."), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "flex-end"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: BTNP,
      onClick: this.props.onClose
    }, "Done")));
  }
  pickGap(i) {
    const g = (this.state.coverage && this.state.coverage.gaps || [])[i];
    if (!g) return;
    const tz = this.state.tz;
    const a = utcToZoned(new Date(g.startUtc), tz),
      b = utcToZoned(new Date(g.endUtc), tz);
    this.setState({
      gapIdx: i,
      gStartDate: a.date,
      gStartTime: a.time,
      gEndDate: b.date,
      gEndTime: b.time,
      gPreview: null,
      gFill: null,
      error: ""
    });
  }
  setTz(tz) {
    // keep the same instants, re-expressed in the new zone
    const s = this.windowUtc();
    lsSet("plunkGa4Tz", tz);
    const next = {
      tz,
      gPreview: null
    };
    if (s.start) {
      const a = utcToZoned(s.start, tz);
      next.gStartDate = a.date;
      next.gStartTime = a.time;
    }
    if (s.end) {
      const b = utcToZoned(s.end, tz);
      next.gEndDate = b.date;
      next.gEndTime = b.time;
    }
    this.setState(next);
  }
  windowUtc() {
    const {
      gStartDate,
      gStartTime,
      gEndDate,
      gEndTime,
      tz
    } = this.state;
    let start = null,
      end = null;
    try {
      start = zonedToUtc(gStartDate, gStartTime, tz);
      end = zonedToUtc(gEndDate, gEndTime, tz);
    } catch (e) {/* invalid */}
    return {
      start,
      end
    };
  }
  async ensureCoverageFor(start, end) {
    const c = this.state.coverage;
    if (!start || !end) return;
    const f = c ? new Date(c.fromUtc).getTime() : 0,
      t = c ? new Date(c.toUtc).getTime() : 0;
    if (c && start.getTime() - 12 * 3600e3 >= f && end.getTime() + 12 * 3600e3 <= t) return;
    const from = new Date(Math.max(start.getTime() - 7 * 86400e3, end.getTime() - 30 * 86400e3));
    const to = new Date(Math.min(end.getTime() + 86400e3, Date.now()));
    const cov = await safe(svc(`/coverage?from=${encodeURIComponent(isoZ(from))}&to=${encodeURIComponent(isoZ(to))}`).getAsync(), null);
    if (cov && this._alive) this.setState({
      coverage: {
        ...cov,
        gaps: this.state.coverage && this.state.coverage.gaps || cov.gaps
      }
    });
  }
  gapBody() {
    const {
      start,
      end
    } = this.windowUtc();
    const body = {
      scheduleId: this.state.schedule ? this.state.schedule.id : null,
      startUtc: isoZ(start),
      endUtc: isoZ(end),
      importGeo: true,
      importConversions: !!this.state.gConv
    };
    if (!this.state.schedule || this.state.editConn || !this.state.hasKey) body.propertyId = this.state.propertyId.trim();
    if (this.state.saJson.trim()) body.serviceAccountJson = this.state.saJson.trim();
    return body;
  }
  async refreshSchedule() {
    const schedules = await safe(svc("/schedules?provider=HistoricalImport").getAsync(), []);
    const sched = (schedules || [])[0] || null;
    if (sched) {
      const st = parseSettings(sched);
      this.setState({
        schedule: sched,
        propertyId: st.Ga4PropertyId || this.state.propertyId,
        hasKey: !!st.hasServiceAccount,
        saJson: "",
        editConn: false
      });
    }
    return sched;
  }
  renderGap() {
    const {
      coverage,
      backfills,
      tz,
      working,
      error,
      gPreview,
      gFill,
      confirmRemove
    } = this.state;
    if (!coverage && !backfills) return /*#__PURE__*/React.createElement("p", null, "Checking your tracking...");
    const canManage = backfills ? backfills.canManage : false;
    const ready = backfills ? backfills.tablesReady : false;
    const gaps = coverage && coverage.gaps || [];
    const {
      start,
      end
    } = this.windowUtc();
    const recent = end && end.getTime() > Date.now() - 48 * 3600e3;
    const hrs = start && end ? Math.round((end - start) / 360000) / 10 : 0;
    // A changed window or conversions choice voids the preview: a fill always matches what was previewed.
    const windowChanged = gPreview && gPreview._body && start && end && (gPreview._body.startUtc !== isoZ(start) || gPreview._body.endUtc !== isoZ(end) || gPreview._body.importConversions !== !!this.state.gConv);
    return /*#__PURE__*/React.createElement("div", null, !ready ? /*#__PURE__*/React.createElement("div", {
      style: S.warn
    }, backfills && backfills.message || "This workspace isn't set up for gap fills yet. Contact Plunk support to enable it.") : null, ready && !canManage ? /*#__PURE__*/React.createElement("div", {
      style: S.warn
    }, "Only an owner or administrator can fill or remove gaps. You can still look at your tracking coverage.") : null, this.renderConnection(), /*#__PURE__*/React.createElement("b", {
      style: {
        fontSize: "12px"
      }
    }, "Gaps we found in the last 14 days"), gaps.length ? /*#__PURE__*/React.createElement("div", {
      style: {
        margin: "6px 0 10px"
      }
    }, gaps.map((g, i) => /*#__PURE__*/React.createElement("label", {
      key: i,
      style: {
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        fontSize: "13px",
        margin: "4px 0",
        cursor: "pointer"
      }
    }, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "gap",
      checked: this.state.gapIdx === i,
      onChange: () => this.pickGap(i),
      style: TICK
    }), fmtZoned(g.startUtc, tz), " to ", fmtZoned(g.endUtc, tz), " (", g.hours, " hours, about ", n0(g.priorWeekViews), " views the week before)", (g.backfillIds || []).length ? /*#__PURE__*/React.createElement("span", {
      style: {
        color: "#166534"
      }
    }, " \xB7 filled") : null))) : /*#__PURE__*/React.createElement("p", {
      style: S.hint
    }, "No gaps found: live tracking was steady. You can still enter a window below."), /*#__PURE__*/React.createElement("label", {
      style: S.lab
    }, "Times are in", /*#__PURE__*/React.createElement("select", {
      style: {
        ...S.inp,
        width: "auto",
        marginLeft: "8px"
      },
      value: tz,
      onChange: e => this.setTz(e.target.value)
    }, [...new Set([tz, ...TIME_ZONES])].map(z => /*#__PURE__*/React.createElement("option", {
      key: z,
      value: z
    }, tzLabel(z))))), /*#__PURE__*/React.createElement("div", {
      style: S.row
    }, /*#__PURE__*/React.createElement("label", {
      style: {
        ...S.lab,
        flex: "1 1 120px"
      }
    }, "Start date", /*#__PURE__*/React.createElement("input", {
      style: S.inp,
      type: "date",
      value: this.state.gStartDate,
      onChange: e => this.setState({
        gStartDate: e.target.value,
        gPreview: null
      })
    })), /*#__PURE__*/React.createElement("label", {
      style: {
        ...S.lab,
        flex: "1 1 90px"
      }
    }, "Start time", /*#__PURE__*/React.createElement("input", {
      style: S.inp,
      type: "time",
      value: this.state.gStartTime,
      onChange: e => this.setState({
        gStartTime: e.target.value,
        gPreview: null
      })
    })), /*#__PURE__*/React.createElement("label", {
      style: {
        ...S.lab,
        flex: "1 1 120px"
      }
    }, "End date", /*#__PURE__*/React.createElement("input", {
      style: S.inp,
      type: "date",
      value: this.state.gEndDate,
      onChange: e => this.setState({
        gEndDate: e.target.value,
        gPreview: null
      })
    })), /*#__PURE__*/React.createElement("label", {
      style: {
        ...S.lab,
        flex: "1 1 90px"
      }
    }, "End time", /*#__PURE__*/React.createElement("input", {
      style: S.inp,
      type: "time",
      value: this.state.gEndTime,
      onChange: e => this.setState({
        gEndTime: e.target.value,
        gPreview: null
      })
    }))), start && end ? /*#__PURE__*/React.createElement("p", {
      style: S.hint
    }, "That is ", isoZ(start).replace("T", " ").replace(":00Z", ""), " to ", isoZ(end).replace("T", " ").replace(":00Z", ""), " UTC (", hrs, " hours).", " ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.ensureCoverageFor(start, end)
    }, "Show tracking around this window")) : null, /*#__PURE__*/React.createElement(CoverageStrip, {
      coverage: coverage,
      startUtc: start,
      endUtc: end
    }), /*#__PURE__*/React.createElement("label", {
      style: {
        fontSize: "13px",
        display: "block",
        margin: "8px 0 4px"
      }
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!this.state.gConv,
      onChange: e => this.setState({
        gConv: e.target.checked
      }),
      style: {
        ...TICK,
        marginRight: "8px"
      }
    }), "Include conversions (GA4 key events)"), /*#__PURE__*/React.createElement("p", {
      style: {
        ...S.hint,
        marginTop: 0
      }
    }, "Untick when your Google Analytics conversions aren't reliable, for example an event that fires on every page. Page views, sessions, sources and cities are still filled; conversions for the gap stay at zero."), recent ? /*#__PURE__*/React.createElement("div", {
      style: S.warn
    }, "The end is less than 48 hours ago. Google Analytics may still be processing those hours, so numbers can go up. You can fill now and fill again later with the same start time; the newer fill replaces the older one.") : null, error && /*#__PURE__*/React.createElement("p", {
      style: S.err
    }, error), gPreview && !windowChanged ? this.renderGapPreview(gPreview) : null, gFill ? /*#__PURE__*/React.createElement("div", {
      style: S.ok
    }, /*#__PURE__*/React.createElement("b", null, "Gap filled."), " Reports include it now; dashboards refresh within a few minutes. Remove it any time from the list below.") : null, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "space-between",
        gap: "8px",
        marginTop: "10px",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: BTN,
      disabled: working,
      onClick: () => this.setState({
        mode: null,
        step: 1,
        error: ""
      })
    }, "Back"), /*#__PURE__*/React.createElement("span", {
      style: {
        display: "flex",
        gap: "8px"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: gPreview && !windowChanged ? BTN : BTNP,
      disabled: working || !canManage || !this.connReady() || !start || !end,
      style: {
        opacity: working || !canManage || !this.connReady() || !start || !end ? 0.5 : 1
      },
      onClick: () => this.runGap("preview")
    }, working && !gPreview ? "Checking with Google..." : "Preview"), /*#__PURE__*/React.createElement("button", {
      className: BTNP,
      disabled: working || !canManage || !ready || !gPreview || windowChanged || gPreview.tablesReady === false,
      style: {
        opacity: working || !canManage || !ready || !gPreview || windowChanged || gPreview.tablesReady === false ? 0.5 : 1
      },
      title: !gPreview ? "Preview first" : "",
      onClick: () => this.runGap("fill")
    }, working && gPreview ? "Filling..." : "Fill the gap"))), backfills && (backfills.items || []).length ? /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: "16px"
      }
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        fontSize: "12px"
      }
    }, "Gap fills"), /*#__PURE__*/React.createElement("p", {
      style: S.hint
    }, "Each fill is tagged, so removing it deletes only its own numbers and rebuilds those days from live tracking."), /*#__PURE__*/React.createElement("div", {
      style: {
        overflowX: "auto"
      }
    }, /*#__PURE__*/React.createElement("table", {
      style: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "12px"
      }
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      style: {
        ...S.th,
        textAlign: "left"
      }
    }, "Window (", tz, ")"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }, "Page views"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }, "Status"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }, "Added"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }))), /*#__PURE__*/React.createElement("tbody", null, backfills.items.map(b => /*#__PURE__*/React.createElement("tr", {
      key: b.id
    }, /*#__PURE__*/React.createElement("td", {
      style: {
        ...S.td,
        textAlign: "left"
      },
      title: b.id
    }, fmtZoned(b.startUtc, tz), " to ", fmtZoned(b.endUtc, tz)), /*#__PURE__*/React.createElement("td", {
      style: S.td
    }, n0(b.counts && b.counts.totals && b.counts.totals.pageViews)), /*#__PURE__*/React.createElement("td", {
      style: S.td
    }, b.status === "active" ? "Active" : b.status === "removing" ? "Removing..." : "Removed"), /*#__PURE__*/React.createElement("td", {
      style: S.td
    }, fmtZoned(b.modifiedUtc, tz), b.createdBy ? ` by ${b.createdBy}` : ""), /*#__PURE__*/React.createElement("td", {
      style: S.td
    }, b.status === "active" && canManage ? confirmRemove === b.id ? /*#__PURE__*/React.createElement("span", null, "Remove? ", /*#__PURE__*/React.createElement("a", {
      style: {
        color: "#dc2626",
        cursor: "pointer"
      },
      onClick: () => this.removeBackfill(b.id)
    }, "Yes, remove"), " · ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.setState({
        confirmRemove: null
      })
    }, "Keep")) : /*#__PURE__*/React.createElement("a", {
      style: {
        color: "#dc2626",
        cursor: "pointer"
      },
      onClick: () => this.setState({
        confirmRemove: b.id
      })
    }, "Remove this backfill") : null))))))) : null);
  }
  renderGapPreview(p) {
    const tz = this.state.tz;
    const base = p.baseline && p.baseline.ga4Daily || [];
    const avgDay = base.length ? base.reduce((a, b) => a + (b.pageViews || 0), 0) / base.length : 0;
    const convOn = !(p._body && p._body.importConversions === false);
    const sum = k => (p.perDay || []).reduce((a, d) => a + (d[k] || 0), 0);
    const convShare = convOn && sum("pageViews") > 0 ? sum("conversions") / sum("pageViews") : 0;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: "8px"
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 4px",
        fontSize: "13px"
      }
    }, /*#__PURE__*/React.createElement("b", null, "Preview"), " (nothing written yet) \xB7 fill id ", /*#__PURE__*/React.createElement("code", null, p.backfillId)), p.timeZone && p.timeZone !== tz ? /*#__PURE__*/React.createElement("div", {
      style: S.warn
    }, "Your Google Analytics property reports in ", p.timeZone, ". The window above is in ", tz, "; that's fine, the times are converted exactly.") : null, /*#__PURE__*/React.createElement("div", {
      style: {
        overflowX: "auto"
      }
    }, /*#__PURE__*/React.createElement("table", {
      style: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "12px"
      }
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      style: {
        ...S.th,
        textAlign: "left"
      }
    }, "Day (UTC)"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }, "Hours filled"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }, "Page views"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }, "Sessions"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }, "Conversions"), /*#__PURE__*/React.createElement("th", {
      style: S.th
    }, "Cities"), /*#__PURE__*/React.createElement("th", {
      style: S.th,
      title: "Page views per filled hour, compared with an average day in the week before"
    }, "vs normal"))), /*#__PURE__*/React.createElement("tbody", null, (p.perDay || []).map(d => {
      const rate = d.hoursFilled > 0 && avgDay > 0 ? Math.round(100 * (d.pageViews / (d.hoursFilled / 24)) / avgDay) : null;
      return /*#__PURE__*/React.createElement("tr", {
        key: d.day
      }, /*#__PURE__*/React.createElement("td", {
        style: {
          ...S.td,
          textAlign: "left"
        }
      }, d.day), /*#__PURE__*/React.createElement("td", {
        style: S.td
      }, d.hoursFilled), /*#__PURE__*/React.createElement("td", {
        style: S.td
      }, n0(d.pageViews)), /*#__PURE__*/React.createElement("td", {
        style: S.td
      }, n0(d.sessions)), /*#__PURE__*/React.createElement("td", {
        style: S.td
      }, convOn ? n0(d.conversions) : "not filled"), /*#__PURE__*/React.createElement("td", {
        style: S.td
      }, n0(d.cities)), /*#__PURE__*/React.createElement("td", {
        style: {
          ...S.td,
          color: rate != null && rate < 60 ? "#b45309" : undefined
        }
      }, rate == null ? "" : rate + "%"));
    })))), p.overlap && p.overlap.liveMinutesInWindow === 0 ? /*#__PURE__*/React.createElement("div", {
      style: S.ok
    }, /*#__PURE__*/React.createElement("b", null, "No overlap with live tracking."), " Every minute in the window was checked. Data before and after the window stays exactly as tracked.") : /*#__PURE__*/React.createElement("div", {
      style: S.ok
    }, /*#__PURE__*/React.createElement("b", null, "No double counting."), " ", n0(p.overlap && p.overlap.liveMinutesInWindow), " minutes inside the window have live tracking; those minutes keep live data and are not filled."), p.baseline && p.baseline.ga4ToPlunkRatio != null ? /*#__PURE__*/React.createElement("p", {
      style: S.hint
    }, "In the 7 days before the window, Google Analytics counted ", p.baseline.ga4ToPlunkRatio, "x the page views Plunk tracking did. Filled numbers are Google's, unchanged.") : null, convShare >= 0.25 ? /*#__PURE__*/React.createElement("div", {
      style: S.warn
    }, /*#__PURE__*/React.createElement("b", null, "These conversions look wrong."), " Google Analytics counted ", Math.round(convShare * 100), "% as many conversions as page views, which usually means a conversion event fires on every page. Untick Include conversions and preview again, then check the event in Google Analytics.") : null, convOn && (p.mappedEvents || []).length ? /*#__PURE__*/React.createElement("p", {
      style: S.hint
    }, "Events matched to your Plunk events: ", p.mappedEvents.map(e => e.plunk).join(", "), ".") : null, (p.warnings || []).map((w, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      style: S.warn
    }, w)), /*#__PURE__*/React.createElement("p", {
      style: S.hint
    }, "Google Analytics has no visitor identities, so visitor counts for date ranges that include the gap are estimates, and no contacts are created."));
  }
  render() {
    const {
      mode,
      loading
    } = this.state;
    const title = mode === "gap" ? "Fill a tracking gap" : mode === "history" ? "Import your history" : "Google Analytics import";
    return /*#__PURE__*/React.createElement("div", {
      className: "de-modal-backdrop",
      onClick: () => !this.state.working && this.props.onClose(),
      style: {
        position: "fixed",
        inset: 0,
        background: "rgba(20,24,30,.45)",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        boxSizing: "border-box"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "de-modal",
      style: {
        background: "#fff",
        borderRadius: "10px",
        padding: "20px 24px",
        width: "720px",
        maxWidth: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        boxSizing: "border-box",
        boxShadow: "0 12px 40px rgba(0,0,0,.18)"
      },
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("h3", {
      style: {
        margin: "0 0 2px"
      }
    }, title), loading ? /*#__PURE__*/React.createElement("p", null, "Loading your connection...") : !mode ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      style: S.hint
    }, "Choose what to bring in from Google Analytics (GA4). We import page views, sessions, sources, events and city-level geography. We never import people: GA4 has no personal data."), /*#__PURE__*/React.createElement("div", {
      style: {
        ...S.row,
        margin: "10px 0 14px"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: S.opt(false),
      onClick: () => this.setState({
        mode: "history",
        step: 2
      })
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        display: "block",
        marginBottom: "2px"
      }
    }, "Import history"), /*#__PURE__*/React.createElement("span", {
      style: {
        color: "#6b7280",
        fontSize: "12px"
      }
    }, "Up to 24 months from before your Plunk tracking started.")), /*#__PURE__*/React.createElement("div", {
      style: S.opt(false),
      onClick: () => this.setState({
        mode: "gap"
      }, this.loadGap)
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        display: "block",
        marginBottom: "2px"
      }
    }, "Fill a tracking gap"), /*#__PURE__*/React.createElement("span", {
      style: {
        color: "#6b7280",
        fontSize: "12px"
      }
    }, "Fill a stretch with no live tracking. Only the minutes with no live data are filled."))), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        justifyContent: "flex-end"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: BTN,
      onClick: this.props.onClose
    }, "Cancel"))) : mode === "history" ? this.renderHistory() : this.renderGap()));
  }
}
export default ImportWizard;